import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { createMaterial, getMaterial, updateMaterial } from "../../core/material.core";
import { useTranslation } from "../../core/translation.core";
import { Material, MaterialFormData } from "../../types/material";
import { LocalInputDefaultLocale } from "../locale/locale.comp";
import { EditorSection, SubmitPanel, Title } from "../reuseables/editor.comp";
import { EditingMode, LabelAndInput } from "../reuseables/form.comp";
import MaterialAttributeEditor from "./materialAttributeEditor";
import MaterialMediaEditor from "./materialMedia.comp";
import MaterialTypeEditor from "./materialTypeSelector.comp";

export default function MaterialEditor({ editingMode, materialId }: { editingMode: EditingMode; materialId?: string }) {
  const methods = useForm<MaterialFormData>({
    defaultValues: {
      name: {},
      sku: "",
      isActive: true,
    },
  });
  const router = useRouter();
  const { t, msg } = useTranslation();
  const queryClient = useQueryClient();

  const { data: editingMaterial } = useQuery({
    queryKey: ["materialSingle", materialId],
    queryFn: (context) => getMaterial(context.queryKey[1]!),
    enabled: materialId !== undefined && editingMode === EditingMode.edit,
    staleTime: 1, // always stale
  });

  useEffect(() => {
    if (editingMaterial) {
      methods.reset(convertMaterialToForm(editingMaterial));
    }
  }, [editingMaterial, methods]);

  const onSubmit = async (data: MaterialFormData) => {
    switch (editingMode) {
      case EditingMode.create:
        await createMaterial(data);
        await queryClient.invalidateQueries({ queryKey: ["materials"] });
        break;
      case EditingMode.edit:
        await updateMaterial(materialId!, data);
        await queryClient.invalidateQueries({ queryKey: ["materials"] });
        await queryClient.invalidateQueries({ queryKey: ["materialSingle", materialId] });
        break;
      default:
        break;
    }

    router.push("/materials");
  };

  return (
    <form onSubmit={methods.handleSubmit(onSubmit)} className="pb-20">
      <EditorSection>
        <Title>{t(msg.product.generalInfo)}</Title>
        <div className="text-xs flex flex-col gap-4">
          <LocalInputDefaultLocale register={methods.register} fieldName="name" label={t(msg.material.materialName)} />

          <LabelAndInput
            register={methods.register}
            fieldName="sku"
            label={t(msg.product.sku)}
            errors={methods.formState.errors}
            registerOptions={{ required: t(msg.product.skuR) }}
          />
        </div>
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.material.materialType)}</Title>
        <MaterialTypeEditor methods={methods as any} />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.attribute.attributes)}</Title>
        <MaterialAttributeEditor methods={methods as any} />
      </EditorSection>

      <EditorSection>
        <Title>{t(msg.media.media)}</Title>
        <MaterialMediaEditor methods={methods} />
      </EditorSection>
      <SubmitButton editingMode={editingMode} />
    </form>
  );
}

function convertMaterialToForm(material: Material): MaterialFormData {
  return {
    galleries: material.galleries,
    materialTypeId: material.materialTypeId,
    name: material.name,
    galleryIds: material.galleries === undefined ? [] : material.galleries.map((g) => g.id),
    featureMedia: material.featureMedia === undefined ? "" : material.featureMedia.id,
    sku: material.sku,
    attributes: material.attributes,
    isActive: material.isActive,
  };
}

function SubmitButton({ editingMode }: { editingMode: EditingMode }) {
  const { t, msg } = useTranslation();
  const { push } = useRouter();

  function getSubmitBtnText() {
    switch (editingMode) {
      case EditingMode.create:
        return t(msg.material.add);
      case EditingMode.edit:
        return t(msg.material.edit);
    }
  }

  return (
    <SubmitPanel>
      <button type="reset" onClick={() => push("/materials")}>
        {t(msg.form.cancel)}
      </button>
      <input type="submit" value={getSubmitBtnText()} />
    </SubmitPanel>
  );
}
